// App Orchestrator - Main coordination and orchestration logic
// ✅ NO MAGIC STRINGS: Use typed enums and constants

import { EventBus } from "../events/EventBus";
import debugLogger from "../utils/debugLogger";

import {
  OrchestratorStatus,
  ModuleLifecycleEvent,
  OrchestratorEvent,
  MODULE_NAMES,
  ModuleName,
  ModuleInfo,
  OrchestratorConfig,
  OrchestratorState,
  ModuleRegistration,
  OrchestratorMetrics,
  OrchestratorError,
} from "./types";

export class AppOrchestrator {
  private state: OrchestratorState;
  private config: OrchestratorConfig;
  private eventBus: EventBus;
  private moduleRegistrations: Map<ModuleName, ModuleRegistration>;
  private metrics: OrchestratorMetrics;
  private shutdownPromise?: Promise<void>;

  constructor(config: Partial<OrchestratorConfig> = {}) {
    this.config = {
      autoStart: true,
      enableLogging: true,
      enableMetrics: true,
      shutdownTimeout: 5000,
      moduleTimeout: 3000,
      ...config,
    };

    this.state = {
      status: OrchestratorStatus.INITIALIZING,
      modules: new Map(),
      startTime: new Date(),
    };

    this.eventBus = new EventBus();
    this.moduleRegistrations = new Map();
    this.metrics = {
      totalModules: 0,
      activeModules: 0,
      errorModules: 0,
      uptime: 0,
      eventCount: 0,
    };

    this.setupEventHandlers();

    if (this.config.autoStart) {
      void this.start();
    }
  }

  // ✅ NO MAGIC STRINGS: Use typed event names
  private setupEventHandlers(): void {
    this.eventBus.subscribeGlobal(OrchestratorEvent.ORCHESTRATOR_START, () => {
      debugLogger.info("orchestrator", "Orchestrator starting");
    });

    this.eventBus.subscribeGlobal(OrchestratorEvent.ORCHESTRATOR_READY, () => {
      debugLogger.info("orchestrator", "Orchestrator ready");
    });

    this.eventBus.subscribeGlobal(
      OrchestratorEvent.ORCHESTRATOR_ERROR,
      (payload: unknown) => {
        const error = payload as Error;
        debugLogger.error("orchestrator", "Orchestrator error", error);
      },
    );
  }

  public async start(): Promise<void> {
    try {
      debugLogger.info("orchestrator", "Starting App Orchestrator");

      this.state.status = OrchestratorStatus.INITIALIZING;
      this.state.startTime = new Date();

      // ✅ NO MAGIC STRINGS: Use typed event names
      this.eventBus.emitGlobal(OrchestratorEvent.ORCHESTRATOR_START, {});

      await this.initializeModules();

      this.state.status = OrchestratorStatus.READY;
      this.updateMetrics();

      // ✅ NO MAGIC STRINGS: Use typed event names
      this.eventBus.emitGlobal(OrchestratorEvent.ORCHESTRATOR_READY, {});

      debugLogger.info("orchestrator", "App Orchestrator started successfully");
    } catch (error) {
      this.handleError(error as Error);
    }
  }

  public async shutdown(): Promise<void> {
    if (this.shutdownPromise) {
      return this.shutdownPromise;
    }

    this.shutdownPromise = this.performShutdown();
    return this.shutdownPromise;
  }

  private async performShutdown(): Promise<void> {
    try {
      debugLogger.info("orchestrator", "Shutting down App Orchestrator");

      this.state.status = OrchestratorStatus.SHUTDOWN;

      // ✅ NO MAGIC STRINGS: Use typed event names
      this.eventBus.emitGlobal(OrchestratorEvent.ORCHESTRATOR_SHUTDOWN, {});

      await this.shutdownModules();

      debugLogger.info("orchestrator", "App Orchestrator shutdown complete");
    } catch (error) {
      debugLogger.error("orchestrator", "Error during shutdown", error);
    }
  }

  public registerModule(registration: ModuleRegistration): void {
    const { name } = registration;

    if (!this.isValidModuleName(name)) {
      throw new OrchestratorError("INVALID_MODULE_NAME", name);
    }

    if (this.moduleRegistrations.has(name)) {
      throw new OrchestratorError("MODULE_ALREADY_REGISTERED", name);
    }

    if (registration.dependencies) {
      this.validateDependencies(name, registration.dependencies);
    }

    this.moduleRegistrations.set(name, registration);

    const moduleInfo: ModuleInfo = {
      name,
      status: OrchestratorStatus.INITIALIZING,
      dependencies: registration.dependencies || [],
      events: registration.events || [],
      initialized: false,
    };

    this.state.modules.set(name, moduleInfo);
    this.metrics.totalModules++;

    // ✅ NO MAGIC STRINGS: Use typed event names
    this.eventBus.emitGlobal(OrchestratorEvent.MODULE_REGISTERED, { name });

    debugLogger.info("orchestrator", `Module registered: ${name}`);
  }

  public unregisterModule(name: ModuleName): void {
    if (!this.moduleRegistrations.has(name)) {
      throw new OrchestratorError("MODULE_NOT_FOUND", name);
    }

    this.moduleRegistrations.delete(name);
    this.state.modules.delete(name);

    // ✅ NO MAGIC STRINGS: Use typed event names
    this.eventBus.emitGlobal(OrchestratorEvent.MODULE_UNREGISTERED, { name });

    debugLogger.info("orchestrator", `Module unregistered: ${name}`);
  }

  public getModuleInfo(name: ModuleName): ModuleInfo | undefined {
    return this.state.modules.get(name);
  }

  public getAllModules(): ModuleInfo[] {
    return Array.from(this.state.modules.values());
  }

  public getStatus(): OrchestratorStatus {
    return this.state.status;
  }

  public getMetrics(): OrchestratorMetrics {
    this.updateMetrics();
    return { ...this.metrics };
  }

  public getEventBus(): EventBus {
    return this.eventBus;
  }

  private isValidModuleName(name: string): name is ModuleName {
    return Object.values(MODULE_NAMES).includes(name as ModuleName);
  }

  private validateDependencies(
    moduleName: ModuleName,
    dependencies: ModuleName[],
  ): void {
    // Check for circular dependencies
    const visited = new Set<ModuleName>();
    const recursionStack = new Set<ModuleName>();

    const hasCircularDependency = (name: ModuleName): boolean => {
      if (recursionStack.has(name)) {
        return true;
      }

      if (visited.has(name)) {
        return false;
      }

      visited.add(name);
      recursionStack.add(name);

      const module = this.moduleRegistrations.get(name);
      if (module?.dependencies) {
        for (const dep of module.dependencies) {
          if (hasCircularDependency(dep)) {
            return true;
          }
        }
      }

      recursionStack.delete(name);
      return false;
    };

    if (hasCircularDependency(moduleName)) {
      throw new OrchestratorError("CIRCULAR_DEPENDENCY", moduleName);
    }

    // Check if all dependencies exist
    for (const dep of dependencies) {
      if (!this.moduleRegistrations.has(dep)) {
        throw new OrchestratorError(
          "DEPENDENCY_NOT_FOUND",
          moduleName,
          `Dependency ${dep} not found`,
        );
      }
    }
  }

  private async initializeModules(): Promise<void> {
    const modulesToInitialize = this.getModulesInDependencyOrder();

    for (const moduleName of modulesToInitialize) {
      await this.initializeModule(moduleName);
    }
  }

  private getModulesInDependencyOrder(): ModuleName[] {
    const visited = new Set<ModuleName>();
    const result: ModuleName[] = [];

    const visit = (name: ModuleName): void => {
      if (visited.has(name)) {
        return;
      }

      visited.add(name);

      const module = this.moduleRegistrations.get(name);
      if (module?.dependencies) {
        for (const dep of module.dependencies) {
          visit(dep);
        }
      }

      result.push(name);
    };

    // ✅ NO MAGIC STRINGS: Use Array.from() for iteration
    for (const moduleName of Array.from(this.moduleRegistrations.keys())) {
      visit(moduleName);
    }

    return result;
  }

  private async initializeModule(name: ModuleName): Promise<void> {
    const registration = this.moduleRegistrations.get(name);
    const moduleInfo = this.state.modules.get(name);

    if (!registration || !moduleInfo) {
      throw new OrchestratorError("MODULE_NOT_FOUND", name);
    }

    try {
      debugLogger.info("orchestrator", `Initializing module: ${name}`);

      // ✅ NO MAGIC STRINGS: Use typed event names
      this.eventBus.emitGlobal(ModuleLifecycleEvent.MODULE_INITIALIZING, {
        name,
      });

      if (registration.onInitialize) {
        await Promise.race([
          registration.onInitialize(),
          this.createTimeoutPromise(this.config.moduleTimeout),
        ]);
      }

      moduleInfo.status = OrchestratorStatus.READY;
      moduleInfo.initialized = true;
      this.metrics.activeModules++;

      // ✅ NO MAGIC STRINGS: Use typed event names
      this.eventBus.emitGlobal(ModuleLifecycleEvent.MODULE_READY, { name });

      debugLogger.info("orchestrator", `Module initialized: ${name}`);
    } catch (error) {
      this.handleModuleError(name, error as Error);
    }
  }

  private async shutdownModules(): Promise<void> {
    // ✅ NO MAGIC STRINGS: Use Array.from() for iteration
    const modulesToShutdown = Array.from(
      this.moduleRegistrations.keys(),
    ).reverse();

    for (const moduleName of modulesToShutdown) {
      await this.shutdownModule(moduleName);
    }
  }

  private async shutdownModule(name: ModuleName): Promise<void> {
    const registration = this.moduleRegistrations.get(name);
    const moduleInfo = this.state.modules.get(name);

    if (!registration || !moduleInfo) {
      return;
    }

    try {
      debugLogger.info("orchestrator", `Shutting down module: ${name}`);

      // ✅ NO MAGIC STRINGS: Use typed event names
      this.eventBus.emitGlobal(ModuleLifecycleEvent.MODULE_SHUTDOWN, { name });

      if (registration.onShutdown) {
        await Promise.race([
          registration.onShutdown(),
          this.createTimeoutPromise(this.config.shutdownTimeout),
        ]);
      }

      moduleInfo.status = OrchestratorStatus.SHUTDOWN;
      moduleInfo.initialized = false;

      debugLogger.info("orchestrator", `Module shutdown: ${name}`);
    } catch (error) {
      debugLogger.error(
        "orchestrator",
        `Error shutting down module ${name}`,
        error,
      );
    }
  }

  private handleModuleError(moduleName: ModuleName, error: Error): void {
    const moduleInfo = this.state.modules.get(moduleName);
    const registration = this.moduleRegistrations.get(moduleName);

    if (moduleInfo) {
      moduleInfo.status = OrchestratorStatus.ERROR;
      moduleInfo.error = error.message;
      this.metrics.errorModules++;
      this.metrics.activeModules--;
    }

    if (registration?.onError) {
      registration.onError(error);
    }

    // ✅ NO MAGIC STRINGS: Use typed event names
    this.eventBus.emitGlobal(ModuleLifecycleEvent.MODULE_ERROR, {
      name: moduleName,
      error,
    });

    debugLogger.error("orchestrator", `Module error: ${moduleName}`, error);
  }

  private handleError(error: Error): void {
    this.state.status = OrchestratorStatus.ERROR;
    this.state.error = error.message;

    // ✅ NO MAGIC STRINGS: Use typed event names
    this.eventBus.emitGlobal(OrchestratorEvent.ORCHESTRATOR_ERROR, error);

    debugLogger.error("orchestrator", "Orchestrator error", error);
  }

  private createTimeoutPromise(timeout: number): Promise<never> {
    return new Promise((_, reject) => {
      setTimeout(() => {
        reject(new OrchestratorError("TIMEOUT_ERROR"));
      }, timeout);
    });
  }

  private updateMetrics(): void {
    if (this.state.startTime) {
      this.metrics.uptime = Date.now() - this.state.startTime.getTime();
    }

    this.metrics.activeModules = Array.from(this.state.modules.values()).filter(
      (module) => module.status === OrchestratorStatus.READY,
    ).length;

    this.metrics.errorModules = Array.from(this.state.modules.values()).filter(
      (module) => module.status === OrchestratorStatus.ERROR,
    ).length;
  }
}
